import { useEffect, useState } from "react";
import { useAccountApi } from "../api/accountApi";

// #2339 — the ONLY place in scoreadmin-fe allowed to call
// GET /org/{orgId}/member (apps/account-api/src/handlers/orgmember.js,
// listOrgMembers). That endpoint is paginated (querystring `limit`: default
// 25, max 100; response carries `nextCursor`, null once there are no more
// pages) — every call site used to issue ONE request and filter the page
// client-side for role, so any member whose row fell past the first page was
// invisible with no error. Most visibly: scribes missing from the bulk
// "Assign scribes to N classes" dialog once an org's membership grew past a
// page. Bumping `limit` alone does not fix this — the filter is client-side,
// so the page has to cover every PERSON in the org, not every scribe, and an
// org can plausibly cross 100 people (the API's own max), re-breaking a
// bumped-but-fixed limit silently.
//
// This hook follows `nextCursor` until the API reports there are no more
// pages, requesting the max page size (100) so the common org (well under
// that) is a single request.
const MAX_PAGE_SIZE = 100;

export function useOrgMembers(orgId) {
  const accountApi = useAccountApi();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) {
      setMembers([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const all = [];
        let cursor;
        do {
          // Pages must be fetched in order: each page's cursor comes from
          // the previous response, there is nothing to parallelize.
          const res = await accountApi.org.listOrganizationMembers(orgId, MAX_PAGE_SIZE, cursor);
          if (res.data?.status !== "success") break;
          const page = Array.isArray(res.data.data?.members) ? res.data.data.members : [];
          all.push(...page);
          cursor = res.data.data?.nextCursor || undefined;
        } while (cursor);
        if (!cancelled) setMembers(all);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          // Fail closed, not partial: a picker showing half an org's members
          // with no indication a page failed is worse than showing none.
          setMembers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, accountApi]);

  return { members, loading, error };
}

export default useOrgMembers;
